import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { serverUrl } from '../../../Constant/Static/config';
import { AuthContext } from '../../../Auth/AuthContext';
import Loader from '../../../Constant/Custom/Loader';
import PopModal from '../../../Constant/Custom/Models/PopModal';

function Application() {
    const { auth } = useContext(AuthContext);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');
    const [csrfToken, setCsrfToken] = useState('');
    const [visible, setVisible] = useState(true);
    const navigate = useNavigate();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState({ title: '', text: '', icon: '' });
    const [onConfirm, setOnConfirm] = useState(() => () => { });
    const [onCancel, setOnCancel] = useState(() => () => { });
    const [showCancelButton, setShowCancelButton] = useState(false);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const handleCertificateUpload = () => {
        navigate('/dashboard/certificate-upload');
    };

    const downloadApplication = async () => {
        if (!csrfToken) return;
        try {
            const userData = auth.user;
            setDownloading(true);
            if (userData) {
                const response = await axios.post(
                    `${serverUrl}/api/user/doc/applicationPdf`,
                    {
                        appNo: userData.appno,
                        email: userData.email,
                        mobile: userData.mobile,
                        showMarkStatus: true
                    },
                    {
                        headers: { 'CSRF-Token': csrfToken },
                        withCredentials: true,
                        responseType: 'blob',
                    }
                );
                if (response.status === 200) {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", `Application-${userData.appno}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }
            }
        } catch (error) {
            console.error('Error downloading application:', error);
            setError('Failed to download application');
        } finally {
            setLoading(false);
            setDownloading(false);
        }
    };

    const submitApplication = async () => {
        if (!csrfToken || user.submitStatus !== 'P') return;
        try {
            const response = await axios.post(
                `${serverUrl}/api/submit-application`,
                { appno: user.appno, mobile: auth.user.mobile },
                {
                    headers: { 'CSRF-Token': csrfToken },
                    withCredentials: true
                }
            );
            if (response.status === 200) {
                setUser({ ...user, submitStatus: 'Y' });
                setShowCancelButton(false)
                setModalType({
                    title: "Application Submission",
                    icon: "success",
                    text: "Application Submitted Successfully"
                })
                openModal();
                setOnConfirm(() => closeModal)
            }
        } catch (error) {
            console.error('Error submitting application:', error);
            setError('Failed to submit application');
        }
    };

    const handleSubmit = () => {
        setModalType({
            title: "Application Submission",
            icon: "info",
            text: "Are you sure you want to submit the application?"
        })
        openModal();
        setShowCancelButton(true)
        setOnCancel(() => closeModal);
        setOnConfirm(() => submitApplication);
    }


    useEffect(() => {
        const fetchCsrfToken = async () => {
            try {
                const response = await axios.get(`${serverUrl}/api/csrf-token`, {
                    withCredentials: true
                });
                setCsrfToken(response.data.csrfToken);
            } catch (error) {
                console.error('Error fetching CSRF token:', error);
                setError('Failed to fetch CSRF token');
            }
        };
        fetchCsrfToken();
    }, []);

    useEffect(() => {
        const checkSession = async () => {
            try {
                const storedUser = auth.user;
                if (!storedUser) {
                    throw new Error('No active session found');
                }
                if (!storedUser.appno || !storedUser.mobile || !storedUser.email) {
                    throw new Error('Incomplete session data');
                }

                const dataToSend = {
                    appno: storedUser.appno,
                    mobile: storedUser.mobile,
                    email: storedUser.email
                };

                // Fetch user data
                const userResponse = await axios.post(`${serverUrl}/api/user`, dataToSend, { withCredentials: true });
                if (userResponse.status !== 200 || !userResponse.data) {
                    throw new Error('Failed to load user data');
                }

                // Fetch application status
                const statusResponse = await axios.post(`${serverUrl}/api/get-app-status`, dataToSend, { withCredentials: true });
                if (statusResponse.status !== 200 || !statusResponse.data) {
                    throw new Error('Failed to load application status');
                }

                setUser({
                    ...userResponse.data,
                    submitStatus: statusResponse.data.submitStatus
                });
            } catch (error) {
                console.error('Error fetching session:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        checkSession();
    }, [navigate]);

    useEffect(() => {
        const interval = setInterval(() => {
            setVisible((prev) => !prev);
        }, 500); // blink every 500ms

        return () => clearInterval(interval); // cleanup on unmount
    }, []);

    const blinkStyle = {
        cursor: "pointer",
        color: "red",
        textAlign: "center",
        opacity: visible ? "1" : "0.3",
    };

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className='homePage'>
            <div className='welcomeApplicant'>
                {user ? (
                    <>
                        <h1>Welcome, {user.userName}!</h1>
                        <div>
                            <p><b>Application No: </b>{user.appno}</p>
                            <p><b>Email ID: </b>{user.email}</p>
                            <p><b>Date of Reg: </b>{user.dateOfReg}</p>
                        </div>
                    </>
                ) : (
                    <div>No user data available</div>
                )}
            </div>
            <div className='homeGeneralInstruction'></div>
            <div className='Submit-update-link'>
                {user?.submitStatus === 'P' ? (
                    <>
                        <h2>You have successfully completed your payment! One step to submit your Application, </h2>
                        <h3
                            className='blink-me'
                            style={blinkStyle}
                            onClick={handleSubmit}
                        >
                            Click here to submit !!!
                        </h3>
                        <h2 style={{ textAlign: 'center' }}>You can download your application , Here</h2>
                    </>
                ) : user?.submitStatus === 'Y' ? (
                    <h2>You have Successfully Submitted Your Application..!</h2>
                ) : null}
            </div>

            <div className='ProceedButton'>

                {downloading ? (
                    <p>Downloading...</p>
                ) : (
                    <button onClick={downloadApplication}>Download Application</button>
                )}

            </div>
            <div className='ProceedButton' style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                marginTop: '20px'
            }}>
                <h3>Do you want to upload your certificates?</h3>

                <button onClick={handleCertificateUpload}>
                    Upload Certificates
                </button>
            </div>
            <div style={{ position: 'relative', alignSelf: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <PopModal
                    isOpen={isModalOpen}
                    type={modalType}
                    confirmButtonText="OK"
                    onConfirm={onConfirm}
                    showCancelButton={showCancelButton}
                    cancelButtonText="Cancel"
                    onCancel={onCancel}
                />
            </div>
        </div>
    );
}

export default Application;